/**
 * Collapsing a day's bookings to one row per client, for the day agenda.
 *
 * This is a presentation rule this page chose, not a correctness one: a client
 * with three calls on one day is one line on the agenda, with the rest a click
 * away in the activity panel.
 *
 * There used to be a second, earlier collapse here — `latestPerSlotUser`, one
 * row per call — because `GET /fetch-booking-records` returned every status
 * event as its own row. It no longer does: since `1f1b855` a booking is **one
 * row** whose status is the newest `BookingStatusHistory` entry, resolved
 * server-side, so the rows arrive already one per call and that step is gone.
 *
 * No React, no API — unit-testable in isolation.
 */

import { isActiveBooking } from "./bookingStatus";

/** Epoch millis for an ordering field, or null when it can't be read. */
function timeOf(value) {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Whether `candidate` was written after `current`: `created_at DESC, id DESC`,
 * the backend's own ordering. A row with no readable `created_at` never
 * displaces one that has it.
 */
function supersedes(candidate, current) {
  const a = timeOf(candidate?.created_at);
  const b = timeOf(current?.created_at);
  if (a !== b) {
    if (a === null) return false;
    if (b === null) return true;
    return a > b;
  }
  return String(candidate?.id ?? "") > String(current?.id ?? "");
}

/**
 * Identity of the client a row belongs to.
 *
 * `user_id` is the key. The `user_name` fallback keeps a keyless row grouped
 * with the rest of that person's day rather than splitting them, and the final
 * per-row fallback stops every anonymous row collapsing into one.
 */
function clientKey(record, index) {
  return (
    record?.user_id ??
    (record?.user_name ? `name:${record.user_name}` : `row:${record?.id ?? index}`)
  );
}

/** Epoch millis of a row's start, from `startsAt` or the raw field. */
function startOf(record) {
  if (record?.startsAt instanceof Date) {
    const ms = record.startsAt.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  return timeOf(record?.start_time);
}

/**
 * One row per client — their latest call on the day, and how many they have.
 *
 * A client who booked, cancelled, and rebooked has two bookings here (the
 * cancelled one and the new one), and this is what turns those agenda rows
 * into one. The rest of them are not lost, they are behind the row —
 * `ClientActivityPanel` shows every call, which is why collapsing this far is
 * safe.
 *
 * A live call always beats a cancelled one: a client who cancelled a 4pm call
 * but still has a confirmed 1pm one is seeing you at 1pm, and that is the row
 * worth showing. Among calls of the same kind, "latest" is the greatest start
 * time, not the most recently written row: on a day agenda the useful single
 * line is the client's last call of the day. Ties fall back to
 * `created_at DESC, id DESC`.
 *
 * The result is **re-sorted ascending by start time**, since the surviving row
 * is often not the one that claimed its client's first position.
 *
 * @param {Array<Object>} records - One day's calls, from `bucketBookingsByLocalDate`.
 * @returns {Array<Object>} One row per client, each with an added `callCount`.
 */
export function latestPerUser(records) {
  const list = Array.isArray(records) ? records : [];
  if (list.length === 0) return list;

  const byClient = new Map();
  list.forEach((record, index) => {
    const key = clientKey(record, index);
    const held = byClient.get(key);
    if (!held) {
      byClient.set(key, { record, count: 1 });
      return;
    }
    held.count += 1;
    const candidateLive = isActiveBooking(record);
    const heldLive = isActiveBooking(held.record);
    const candidateStart = startOf(record);
    const heldStart = startOf(held.record);
    const later =
      candidateLive !== heldLive
        ? candidateLive
        : candidateStart !== heldStart
        ? candidateStart !== null &&
          (heldStart === null || candidateStart > heldStart)
        : supersedes(record, held.record);
    if (later) held.record = record;
  });

  return [...byClient.values()]
    .map(({ record, count }) => ({ ...record, callCount: count }))
    .sort((a, b) => (startOf(a) ?? 0) - (startOf(b) ?? 0));
}
